import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Messages from "./Messages";
import PageHeader from "../components/PageHeader";
import { API_ENDPOINTS } from "../config/api";
import {
  StateStats,
  StatCard,
  StatValue,
  StatLabel,
  StatDescription
} from "../components/Stats";
import { MapWrapper, MapBoundsFitter } from "../components/Map";
import {
  DataTable,
  TableHeader,
  TableRow,
  TableCell,
  TableCellBold
} from "../components/DataTable";

// Messages Wrapper Component for StatePage
const MessagesWrapper = ({ user, selectedBot, token, onBack, onLogout }) => {
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0
      }}
    >
      <Messages
        user={user}
        selectedBot={selectedBot}
        token={token}
        onBack={onBack}
        onLogout={onLogout}
        isEmbedded={true} // Pass a prop to indicate this is embedded
      />
    </div>
  );
};

// Styled Components
const StatePageContainer = styled.div`
  background: #f8f9fa;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 80px;

  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    height: calc(100vh - 120px);
  }
`;

const StateInfoPanel = styled.div`
  flex: 1 1 50%;
  background: white;
  border-right: 2px solid #e9ecef;
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  min-width: 0;
  min-height: 0;
  gap: 2rem;

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 2px solid #e9ecef;
    padding: 1rem;
    flex: 0 0 auto;
    max-height: 40vh;
  }
`;

const ChatPanel = styled.div`
  flex: 1 1 50%;
  height: calc(100vh - 80px);
  position: relative;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1.125rem;
  color: #6c757d;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
`;

const ErrorState = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 1rem;
  margin-bottom: 1rem;
  max-width: 400px;
`;

// const TopSection = styled.div`
//   display: flex;
//   flex-direction: row;
//   gap: 2rem;

//   > div {
//     flex: 1;
//   }
// `;

// Map states to specific bot IDs
const STATE_BOT_MAPPING = {
  pennsylvania: "pennsylvania-snap-handbook-assistant"
};

// Map state names to FIPS codes for Census API
const STATE_FIPS_MAPPING = {
  pennsylvania: "42"
};

// Census API key
const CENSUS_API_KEY = "7cf7431a16106ebee37728f2e63d1a56acd44427";

function StatePage({ user, onLogout }) {
  const { stateName } = useParams();
  const navigate = useNavigate();
  const [selectedBot, setSelectedBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mauData, setMauData] = useState(null);
  const [, setMauLoading] = useState(false);
  const [mauError, setMauError] = useState(null);
  const [censusData, setCensusData] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);
  const [censusError, setCensusError] = useState(null);
  const [mergedData, setMergedData] = useState(null);

  // Map-related state
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);

  // Format state name with proper capitalization
  const formattedStateName = stateName
    ? stateName
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";

  // Function to fetch Census data
  const fetchCensusData = useCallback(async () => {
    setCensusLoading(true);
    setCensusError(null);

    try {
      const stateKey = stateName?.toLowerCase();
      const stateFips = STATE_FIPS_MAPPING[stateKey];

      if (!stateFips) {
        setCensusError(`Census data not available for ${stateName}`);
        setCensusLoading(false);
        return;
      }

      const censusUrl = `https://api.census.gov/data/2021/acs/acs5?get=B01003_001E,STATE,COUNTY&for=county:*&in=state:${stateFips}&key=${CENSUS_API_KEY}`;

      const response = await fetch(censusUrl);
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 1) {
          // Skip the header row and process the data
          const processedData = data.slice(1).map((row) => ({
            population: parseInt(row[0]),
            stateCode: row[1],
            countyCode: row[2],
            fullFips: row[1] + row[2] // Combine state + county for full FIPS code (e.g., "42001")
          }));
          setCensusData(processedData);
        } else {
          setCensusError("No census data found");
        }
      } else {
        setCensusError("Failed to load census data from server");
      }
    } catch (error) {
      console.error("Error fetching census data:", error);
      setCensusError("Failed to load census data. Please try again.");
    } finally {
      setCensusLoading(false);
    }
  }, [stateName]);

  // Function to merge MAU and Census data
  const mergeMauAndCensusData = useCallback(() => {
    if (!mauData || !censusData) {
      return;
    }

    // Create a map from full FIPS code to population
    const fipsToPopulation = censusData.reduce((acc, county) => {
      acc[county.fullFips] = county.population;
      return acc;
    }, {});

    // Merge MAU data with census data
    const merged = mauData.data.map((mauRecord) => {
      // Try to get FIPS from the MAU data - check various possible field names
      const fipsCode =
        mauRecord.FIPS ||
        mauRecord.fips ||
        mauRecord.COUNTY_FIPS ||
        mauRecord.county_fips ||
        mauRecord.FIPS_CODE ||
        mauRecord.fips_code ||
        mauRecord.CountyFIPS ||
        mauRecord.county_fips_code ||
        mauRecord.STATE_COUNTY_FIPS ||
        mauRecord.state_county_fips;

      const population = fipsCode ? fipsToPopulation[fipsCode] : null;

      // Parse MAU as number to ensure it's not a string (remove commas first)
      const mauValue =
        parseInt(mauRecord.MAU.toString().replace(/,/g, "")) || 0;

      return {
        ...mauRecord,
        fipsCode,
        population,
        percentage:
          population && mauValue
            ? ((mauValue / population) * 100).toFixed(1)
            : null
      };
    });

    setMergedData(merged);
  }, [mauData, censusData]);

  // Function to fetch MAU data
  const fetchMauData = useCallback(async () => {
    setMauLoading(true);
    setMauError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMauError("Authentication required");
        setMauLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "propel-mau"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMauData(data);
        } else {
          setMauError("Failed to load MAU data");
        }
      } else {
        setMauError("Failed to load MAU data from server");
      }
    } catch (error) {
      console.error("Error fetching MAU data:", error);
      setMauError("Failed to load MAU data. Please try again.");
    } finally {
      setMauLoading(false);
    }
  }, [stateName]);

  // Effect to merge data when both MAU and Census data are available
  useEffect(() => {
    mergeMauAndCensusData();
  }, [mauData, censusData, mergeMauAndCensusData]);

  // Check if leaflet CSS is loaded
  useEffect(() => {
    // Wait a bit for the CSS to be processed
    const checkCSS = () => {
      const leafletCSS = document.querySelector('link[href*="leaflet.css"]');
      const leafletStyles = document.querySelector("style[data-leaflet]");

      if (!leafletCSS && !leafletStyles) {
        // Try to inject the CSS manually if it's not found
        const style = document.createElement("style");
        style.setAttribute("data-leaflet", "true");
        style.textContent = `
          .leaflet-container {
            height: 100% !important;
            width: 100% !important;
          }
          .leaflet-control-zoom {
            display: none !important;
          }
          .leaflet-control-attribution {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    // Check immediately and after a short delay
    checkCSS();
    setTimeout(checkCSS, 100);
  }, []);

  // Function to fetch GeoJSON data for county boundaries
  const fetchGeoJsonData = useCallback(async () => {
    setMapLoading(true);
    setMapError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMapError("Authentication required");
        setMapLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "county-shapes"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGeoJsonData(data.geoJson);
        } else {
          setMapError("Failed to load county boundaries");
        }
      } else {
        setMapError("Failed to load county boundaries from server");
      }
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error);
      setMapError("Failed to load county boundaries");
    } finally {
      setMapLoading(false);
    }
  }, [stateName]);

  // Function to get color based on percentage
  const getColor = (percentage) => {
    if (!percentage || percentage === 0) return "#f7f7f7";

    // Color scale from light to dark red
    const colors = [
      "#fee5d9", // 0-1%
      "#fcbba1", // 1-2%
      "#fc9272", // 2-3%
      "#fb6a4a", // 3-4%
      "#ef3b2c", // 4-5%
      "#cb181d", // 5-6%
      "#a50f15", // 6-7%
      "#67000d" // 7%+
    ];

    const thresholds = [0, 1, 2, 3, 4, 5, 6, 7];
    const numericPercentage = parseFloat(percentage);

    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (numericPercentage >= thresholds[i]) {
        return colors[i];
      }
    }

    return colors[0];
  };

  // Function to style GeoJSON features
  const styleFeature = (feature) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from new GeoJSON data
    const countyData = mergedData?.find((d) => d.fipsCode === fipsCode);
    const percentage = countyData?.percentage || 0;

    // Use color based on percentage, or default gray if no data
    const defaultColor = "#e0e0e0"; // Light gray for no data
    const fillColor = percentage > 0 ? getColor(percentage) : defaultColor;

    return {
      fillColor: fillColor,
      weight: 1,
      opacity: 1,
      color: "#666",
      fillOpacity: 0.7
    };
  };

  // Function to handle feature interactions
  const onEachFeature = (feature, layer) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from new GeoJSON data
    const countyName = feature.properties.NAME; // Use NAME from new GeoJSON data
    const countyData = mergedData?.find((d) => d.fipsCode === fipsCode);

    if (countyData) {
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
          MAU: ${countyData.MAU}<br/>
          Population: ${countyData.population?.toLocaleString() || "N/A"}<br/>
          Percentage: ${countyData.percentage || "N/A"}%
        </div>
      `;
      layer.bindPopup(popupContent);
    } else {
      // Show basic info even if no MAU data
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
          FIPS: ${fipsCode}<br/>
          MAU: No data available
        </div>
      `;
      layer.bindPopup(popupContent);
    }
  };

  useEffect(() => {
    const fetchBotData = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.bots);
        if (response.ok) {
          const data = await response.json();
          const bots = data.bots || [];

          // Get the bot ID for this specific state
          const stateKey = stateName?.toLowerCase();
          const desiredBotId = STATE_BOT_MAPPING[stateKey];

          if (!desiredBotId) {
            setError(`No bot configured for ${stateName || "this state"}`);
            setLoading(false);
            return;
          }

          // Find the bot with the specified ID
          const bot = bots.find((b) => b.id === desiredBotId);

          if (!bot) {
            setError(
              `Bot "${desiredBotId}" not found for ${stateName || "this state"}`
            );
            setLoading(false);
            return;
          }

          setSelectedBot(bot);
        } else {
          setError("Failed to load bots from server");
        }
      } catch (error) {
        console.error("Error fetching bot:", error);
        setError("Failed to load assistant. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (stateName) {
      fetchBotData();
      fetchMauData(); // Fetch MAU data
      fetchCensusData(); // Fetch Census data
      fetchGeoJsonData(); // Fetch GeoJSON data
    } else {
      setError("State not found");
      setLoading(false);
    }
  }, [stateName, fetchMauData, fetchCensusData, fetchGeoJsonData]);

  const handleBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <StatePageContainer>
        <PageHeader
          title={`${formattedStateName} × Propel Dashboard`}
          showBackButton={true}
          onBack={handleBack}
        />
        <LoadingContainer>
          Loading {formattedStateName || "state"} info...
        </LoadingContainer>
      </StatePageContainer>
    );
  }

  if (error) {
    return (
      <StatePageContainer>
        <PageHeader
          title={`${formattedStateName} × Propel Dashboard`}
          showBackButton={true}
          onBack={handleBack}
        />
        <ErrorContainer>
          <ErrorState>{error || "State not found"}</ErrorState>
          <button onClick={handleBack}>Go Back</button>
        </ErrorContainer>
      </StatePageContainer>
    );
  }

  const dataToDisplay = mergedData || (mauData ? mauData.data : []);

  return (
    <StatePageContainer>
      <PageHeader
        title={`${formattedStateName} × Propel Dashboard`}
        showBackButton={true}
        onBack={handleBack}
      />
      <ContentContainer>
        <StateInfoPanel>
          <StateStats>
            <StatCard>
              <StatValue size="large">
                {mauData?.totalMAU || "Loading..."}
              </StatValue>
              <StatLabel size="large">
                Propel users in {formattedStateName}
              </StatLabel>
              <StatDescription>
                Monthly active users as of{" "}
                {new Date(mauData?.dataDate).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                })}
              </StatDescription>
            </StatCard>
          </StateStats>

          <MapWrapper minHeight="24rem">
            {mapLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  background: "#f8f9fa"
                }}
              >
                Loading map...
              </div>
            )}
            {mapError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  background: "#f8d7da",
                  color: "#721c24",
                  padding: "1rem"
                }}
              >
                {mapError}
              </div>
            )}
            {geoJsonData &&
              !mapLoading &&
              !mapError &&
              (() => {
                return (
                  <MapContainer
                    scrollWheelZoom={false}
                    zoomControl={false}
                    doubleClickZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                      attribution=""
                    />
                    <GeoJSON
                      data={geoJsonData}
                      style={styleFeature}
                      onEachFeature={onEachFeature}
                    />
                    <MapBoundsFitter geoJsonData={geoJsonData} />
                  </MapContainer>
                );
              })()}
          </MapWrapper>

          {(mauError || censusError) && (
            <div
              style={{
                background: "#f8d7da",
                color: "#721c24",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                marginBottom: "1rem"
              }}
            >
              {mauError || censusError}
            </div>
          )}
          {mauData && (
            <DataTable>
              <TableHeader>
                <div>County</div>
                <div>Propel users</div>
                <div>Total population</div>
                <div>% of population</div>
              </TableHeader>
              {dataToDisplay.slice(0, 10).map((record, index) => (
                <TableRow key={index}>
                  <TableCellBold>{record.COUNTY_NAME}</TableCellBold>
                  <TableCell>{record.MAU}</TableCell>
                  <TableCell>
                    {record.population
                      ? record.population.toLocaleString()
                      : censusLoading
                      ? "Loading..."
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {record.percentage
                      ? `${record.percentage}%`
                      : censusLoading
                      ? "Loading..."
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
              {dataToDisplay.length > 10 && (
                <TableRow>
                  <TableCell style={{ color: "#6c757d" }}>
                    {dataToDisplay.length - 10} more counties
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </DataTable>
          )}
        </StateInfoPanel>

        <ChatPanel>
          {selectedBot && (
            <MessagesWrapper
              user={user}
              selectedBot={selectedBot}
              token={localStorage.getItem("token")}
              onBack={handleBack}
              onLogout={onLogout}
            />
          )}
        </ChatPanel>
      </ContentContainer>
    </StatePageContainer>
  );
}

export default StatePage;
